import {first, isEqual} from 'lodash';
import {ReactNode} from 'react';
import {connect} from 'react-redux';

import {hasTicketingFilters} from '../../../../libs/inboxes/business/src/ticketing/filters';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {UnwrappedActionCreator} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {
  InboxesPanelId,
  makeCellsPanelId,
  makeViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {makeGetCellsFilters} from '../../../../srcCommon/inboxes/cells/cellsFiltersSelectors';
import {InboxesFiltersEnum, RInboxesFilter} from '../../../models/url/models/inboxesUrlModel';
import {
  Filter,
  updateSelectedFilterAction,
} from '../../../store/data/localPreferences/localPreferencesActions';
import {
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
  makeGetCellsPanelPage,
} from '../../../store/panels/cells/cellsPanelSelectors';
import {makeGetConversationSelection} from '../../../store/panels/cells/selectors/cellsPanelSelectionSelectors';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  isInboxesConversationsParameters,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {clearInboxesParametersSelection} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {CellsHeaderFiltersProps} from '../cells/header/cellsHeaderFilters';
import {InboxesTopbar} from './inboxesTopbar';

/*
 * Props.
 */

interface InboxesTopbarContainerProps {
  inboxesPanelId: InboxesPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: InboxesTopbarContainerProps) {
  return makeCellsPanelId(props.inboxesPanelId);
}

function makeGetCellsContext<P>(getCellsPanelPage: CellsPanelPageSelector<P>) {
  return createSelector([getCellsPanelPage], (page) => page.cellsContext);
}

function makeGetCellsInboxesParameters<P>(getCellsPanelPage: CellsPanelPageSelector<P>) {
  return createSelector([getCellsPanelPage], (page) => page.parameters);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InboxesTopbarContainerProps>()(() => {
  const getCellsPanelPage = makeGetCellsPanelPage(getCellsPanelId);
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);
  const getCellsPanelPageParameters = makeGetCellsInboxesParameters(getCellsPanelPage);
  const getConversationSelection = makeGetConversationSelection(getCellsPanelPageParameters, getCellsPanelId);

  return {
    cellsContext: makeGetCellsContext(getCellsPanelCurrentPage),
    parameters: getCellsPanelPageParameters,
    filters: makeGetFilterProps(getCellsPanelPage, getCellsPanelCurrentPage),
    conversationSelection: getConversationSelection,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<InboxesTopbarContainerProps>()(() => ({
  navigateToLinkAction,
  updateSelectedFilterAction,
}));

export const InboxesTopbarContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {parameters, filters: stateFilters} = stateProps;

    return {
      ...ownProps,
      ...stateProps,
      filters: makeCellsFilterProps(parameters, stateFilters, dispatchProps.navigateToLinkAction),
      panelId: makeViewerPanelId(ownProps.inboxesPanelId),
    };
  },
)(InboxesTopbar);

function makeCellsFilterProps(
  parameters: InboxesParameters,
  stateFilters: FilterProps | undefined,
  navigateToLink: UnwrappedActionCreator<typeof navigateToLinkAction>,
): CellsHeaderFiltersProps<Filter> | undefined {
  if (!stateFilters) {
    return undefined;
  }
  return {
    ...stateFilters,
    onMultipleFiltersSelect: (newSelectedFilters) => {
      // Filters only apply to conversation lists.
      if (!isInboxesConversationsParameters(parameters)) {
        return;
      }

      const newFilters = newSelectedFilters.filter(RInboxesFilter.is);

      // Multiple filters only apply to inbox filters.
      if (newFilters.length === 0) {
        return;
      }

      // Track chosen filters
      trackInterfaceEvent(InterfaceEventsEnum.CELLS_FILTERS_SELECTED, {
        tab_names: newFilters,
        tab_count: newFilters.length,
      });

      const newParameters: InboxesConversationsParameters = {
        ...clearInboxesParametersSelection(parameters),
        filters: newFilters,
      };
      navigateToLink(buildParametersPath(newParameters));
    },
    disableMultipleFiltersSelect: hasTicketingFilters(stateFilters.filters),
  };
}

function makeGetFilterProps<P>(
  getPage: CellsPanelPageSelector<P>,
  getCurrentPage: CellsPanelPageSelector<P>,
): AppParametricSelector<P, FilterProps | undefined> {
  const getFilters = makeGetCellsFilters(getPage);
  const getCurrentFilters = makeGetCellsFilters(getCurrentPage);

  return (state, props) => {
    const page = getPage(state, props);
    const currentPage = getCurrentPage(state, props);

    // If there's only one page, use its filters.
    const filters = getFilters(state, props);
    if (currentPage === page) {
      return computeFilterProps(page.parameters, filters);
    }

    // Otherwise, compare their filters.
    // We only use the page filters if they match.
    const currentFilters = getCurrentFilters(state, props);
    if (isEqual(currentFilters, filters)) {
      return computeFilterProps(page.parameters, filters);
    }

    // Otherwise, use the current filters.
    return computeFilterProps(currentPage.parameters, currentFilters);
  };
}

interface FilterProps {
  filters: ReadonlyArray<InboxesFiltersEnum>;
  selectedFilter: Filter;
  selectedFilters: ReadonlyArray<InboxesFiltersEnum>;
  localSearchFilterName?: ReactNode;
}

function computeFilterProps(
  parameters: InboxesParameters,
  filters: ReadonlyArray<InboxesFiltersEnum>,
): FilterProps | undefined {
  // Some folders don't have any filters.
  const defaultFilter = first(filters);
  if (!defaultFilter) {
    return undefined;
  }

  // Make sure at least one filter is selected.
  const selectedFilters =
    isInboxesConversationsParameters(parameters) && parameters.filters.length > 0
      ? parameters.filters
      : [defaultFilter];

  return {
    filters,
    selectedFilter: selectedFilters[0],
    selectedFilters,
  };
}
